import type { Directive } from "vue";

type FlySource = HTMLElement & { __flyHandler?: () => void };
type FlyTarget = HTMLElement | string;

interface FlyOptions {
	target: FlyTarget;
	duration?: number;
	easing?: string;
	scale?: number;
	scroll?: boolean;
	canAnimate?: () => boolean;
}

const resolveTarget = (target: FlyTarget) => {
	if (typeof target === "string") return document.querySelector(target) as HTMLElement;

	return target;
};

export const runFlyAnimation = (sourceEl: HTMLElement, options: FlyOptions) => {
	const targetEl = resolveTarget(options.target);
	if (!targetEl) return;
	if (options?.canAnimate && !options.canAnimate()) return;
	if (options?.scroll) targetEl.scrollIntoView({ behavior: "smooth", inline: "center" });

	const sourceRect = sourceEl.getBoundingClientRect();
	const targetRect = targetEl.getBoundingClientRect();

	const clone = sourceEl.cloneNode(true) as HTMLElement;

	clone.style.position = "fixed";
	clone.style.left = `${sourceRect.left}px`;
	clone.style.top = `${sourceRect.top}px`;
	clone.style.width = `${sourceRect.width}px`;
	clone.style.height = `${sourceRect.height}px`;
	clone.style.margin = "0";
	clone.style.zIndex = "9999";
	clone.style.pointerEvents = "none";
	clone.style.transformOrigin = "top left";

	document.body.appendChild(clone);

	const dx = targetRect.left - sourceRect.left;
	const dy = targetRect.top - sourceRect.top;

	const scale = options.scale ?? Math.min(targetRect.width / sourceRect.width, targetRect.height / sourceRect.height);

	const animation = clone.animate(
		[
			{ transform: "translate(0,0) scale(1)", opacity: 1 },
			{ transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 },
		],
		{ duration: options.duration ?? 1200, easing: options.easing ?? "cubic-bezier(0.2, 0.8, 0.2, 1)", fill: "forwards" },
	);

	const cleanup = () => {
		clone.remove();
	};

	animation.finished.then(cleanup).catch(cleanup);
	animation.oncancel = cleanup;
};

export const vFlyTo: Directive<FlySource, FlyTarget | FlyOptions> = {
	mounted(el, binding) {
		const handler = () => {
			const value = binding.value;
			if (!value) return;

			if (value instanceof HTMLElement || typeof value === "string") {
				runFlyAnimation(el, { target: value });
				return;
			}

			runFlyAnimation(el, value);
		};

		el.__flyHandler = handler;
		el.addEventListener("click", handler);
	},

	unmounted(el) {
		const handler = el.__flyHandler;
		if (handler) el.removeEventListener("click", handler);
	},
};
